import { z } from "zod";
import {
  assistantCodegenJobPrepareSchema,
  assistantCodegenJobReferenceSchema,
} from "./assistant-codegen-job";
import { assistantContextSchema } from "./assistant-protocol";
/** Correlation only. Actual native editor liveness is checked by its adapter. */
export function codegenContextCanonicalText(raw: unknown) {
  const c = assistantContextSchema.parse(raw);
  return JSON.stringify({
    branchId: c.branchId,
    changeCounter: c.changeCounter,
    editorInstanceId: c.editorInstanceId,
    projectId: c.projectId,
    revisionNum: c.revisionNum,
    versionId: c.versionId,
  });
}
export const codegenRecoveryContextSchema = assistantContextSchema;
export type NativeCodegenRecoveryContext = z.infer<
  typeof assistantContextSchema
>;
export const codegenHttpPrepareSchema = z
  .object({
    context: assistantContextSchema,
    input: assistantCodegenJobPrepareSchema,
  })
  .strict();
export const codegenHttpExecuteSchema = z
  .object({
    context: assistantContextSchema,
    reference: assistantCodegenJobReferenceSchema,
  })
  .strict();
export const codegenHttpReadSchema = codegenHttpExecuteSchema
  .extend({ mode: z.enum(["normal", "owned_recovery"]).default("normal") })
  .strict();
const sha = z.string().regex(/^[a-f0-9]{64}$/);
export const codegenHttpManifestSchema = codegenHttpReadSchema
  .extend({
    cursor: z.number().int().min(0).max(4096).default(0),
    limit: z.number().int().min(1).max(32).default(16),
  })
  .strict();
export const codegenHttpFileSchema = codegenHttpReadSchema
  .extend({
    artifactSha256: sha,
    manifestSha256: sha,
    fileIndex: z.number().int().min(0).max(4095),
    offset: z.number().int().nonnegative(),
    length: z
      .number()
      .int()
      .min(1)
      .max(128 * 1024),
  })
  .strict();
export const codegenFileMetadataSchema = z
  .object({
    index: z.number().int().nonnegative(),
    path: z.string().min(1),
    encoding: z.enum(["utf8", "base64"]),
    size: z.number().int().nonnegative(),
    sha256: sha,
  })
  .strict();
const receipt = assistantCodegenJobReferenceSchema
  .extend({
    schemaVersion: z.literal(1),
    kind: z.literal("codegen_artifact"),
    actorUserId: z.string().min(1),
    attemptId: z.string().min(1),
    artifactSha256: sha,
    manifestSha256: sha,
  })
  .strict();
const source = z
  .object({
    projectId: z.string().min(1),
    sourceId: z.string().min(1),
    projectRevId: z.string().min(1),
    revision: z.number().int().positive(),
    branchId: z.string().nullable(),
    version: z.string(),
    sourceSha256: sha,
    dependencyIds: z.array(z.string()),
  })
  .strict();
const rootInfo = z
  .object({
    profile: z.literal("actor_bound_root_snapshot_v1"),
    projectId: z.string().min(1),
    projectRevId: z.string().min(1),
    revision: z.number().int().positive(),
    sourceSha256: sha,
    optionsSha256: sha,
    metadataId: z.string().min(1),
    configSha256: sha,
    runtimeFlagsSha256: sha,
    manifestSha256: sha,
    delivery: z.literal("inline_native_output"),
    fileCount: z.number().int().min(1).max(4096),
    totalBytes: z
      .number()
      .int()
      .min(0)
      .max(16 * 1024 * 1024),
  })
  .strict();
const familyInfo = z
  .object({
    profile: z.literal("actor_bound_native_graph_v1"),
    projectId: z.string().min(1),
    generationOptions: z
      .object({
        platform: z.enum(["react", "nextjs", "gatsby", "tanstack"]),
        platformVersion: z.string().optional(),
        platformOptions: z
          .object({
            nextjs: z.object({ appDir: z.boolean() }).strict().optional(),
          })
          .strict(),
        stylesOpts: z
          .object({ scheme: z.enum(["css", "css-modules"]) })
          .strict(),
      })
      .strict(),
    closureSha256: sha,
    sourceCount: z.number().int().min(1).max(65),
    manifestSha256: sha,
    delivery: z.literal("inline_native_graph"),
    fileCount: z.number().int().min(1).max(4096),
    totalBytes: z
      .number()
      .int()
      .min(0)
      .max(16 * 1024 * 1024),
  })
  .strict();
export const codegenHttpSummarySchema = z
  .object({
    reference: assistantCodegenJobReferenceSchema,
    state: z.enum(["prepared", "outcome_unknown", "applied", "cancelled"]),
    receipt: receipt.nullable(),
    artifact: z.union([rootInfo, familyInfo]).nullable(),
  })
  .strict()
  .superRefine((v, c) => {
    if (
      (v.state === "applied") !== (v.receipt !== null && v.artifact !== null) ||
      (v.receipt === null) !== (v.artifact === null)
    )
      c.addIssue({
        code: "custom",
        message: "Durable receipt/artifact pair required",
      });
  });
export const codegenHttpManifestOutputSchema = z
  .object({
    summary: codegenHttpSummarySchema,
    files: z.array(codegenFileMetadataSchema).max(32),
    nextCursor: z.number().int().min(0).max(4096).nullable(),
  })
  .strict();
export const codegenHttpFileOutputSchema = z
  .object({
    reference: assistantCodegenJobReferenceSchema,
    artifactSha256: sha,
    manifestSha256: sha,
    fileIndex: z.number().int().min(0).max(4095),
    path: z.string().min(1),
    originalEncoding: z.enum(["utf8", "base64"]),
    size: z.number().int().nonnegative(),
    sha256: sha,
    offset: z.number().int().nonnegative(),
    length: z
      .number()
      .int()
      .min(0)
      .max(128 * 1024),
    bytes: z.string().max(174764),
    nextOffset: z.number().int().nonnegative().nullable(),
  })
  .strict()
  .superRefine((value, ctx) => {
    if (
      (value.length === 0 || value.size === 0) &&
      !(
        value.length === 0 &&
        value.size === 0 &&
        value.offset === 0 &&
        value.bytes === "" &&
        value.nextOffset === null
      )
    )
      ctx.addIssue({
        code: "custom",
        path: ["length"],
        message: "Zero-length response requires an exact empty native file",
      });
  });

const nativeFile = z
  .object({
    path: z.string().min(1),
    bytes: z.string(),
    encoding: z.enum(["utf8", "base64"]),
    size: z.number().int().nonnegative(),
    sha256: sha,
  })
  .strict();
const rootArtifact = rootInfo
  .omit({ fileCount: true, totalBytes: true })
  .extend({ files: z.array(nativeFile).min(1).max(4096) })
  .strict();
const familyArtifact = familyInfo
  .omit({ fileCount: true, totalBytes: true, sourceCount: true })
  .extend({
    sources: z.array(source).min(1).max(65),
    files: z.array(nativeFile).min(1).max(4096),
  })
  .strict();
export const codegenHttpReadOutputSchema = z
  .object({
    reference: assistantCodegenJobReferenceSchema,
    state: z.enum(["prepared", "outcome_unknown", "applied", "cancelled"]),
    receipt: receipt.nullable(),
    artifact: z.union([rootArtifact, familyArtifact]).nullable(),
  })
  .strict();
export const codegenHttpSourceSchema = codegenHttpReadSchema
  .extend({
    sourceIndex: z.number().int().min(0).max(64),
    dependencyCursor: z.number().int().nonnegative().default(0),
    limit: z.number().int().min(1).max(32).default(16),
  })
  .strict();
export const codegenHttpSourceOutputSchema = z
  .object({
    reference: assistantCodegenJobReferenceSchema,
    manifestSha256: sha,
    closureSha256: sha,
    sourceIndex: z.number().int().min(0).max(64),
    source: source
      .omit({ dependencyIds: true })
      .extend({
        dependencyCount: z.number().int().nonnegative(),
        dependencyIds: z.array(z.string()).max(32),
      })
      .strict(),
    nextDependencyCursor: z.number().int().nonnegative().nullable(),
  })
  .strict();

export function summarizeCodegenHttpRead(raw: unknown) {
  const result = codegenHttpReadOutputSchema.parse(raw),
    a = result.artifact;
  const artifact = a
    ? (() => {
        if (a.profile === "actor_bound_native_graph_v1") {
          const { files, sources, ...metadata } = a;
          return {
            ...metadata,
            sourceCount: sources.length,
            fileCount: files.length,
            totalBytes: files.reduce((n, f) => n + f.size, 0),
          };
        }
        const { files, ...metadata } = a;
        return {
          ...metadata,
          fileCount: files.length,
          totalBytes: files.reduce((n, f) => n + f.size, 0),
        };
      })()
    : null;
  return codegenHttpSummarySchema.parse({ ...result, artifact });
}
